import { useEffect, useRef, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router';
import { Alert, Button, FormControl, FormLabel, Spinner, Text, Textarea } from '@chakra-ui/react';
import { FiCheck } from 'react-icons/fi';

import { api } from '@app/api';
import { MasterOutlet, MasterOutletUpdate } from '@app/api/models/acb';
import { useHeader } from '@app/components/common/navigation/hooks/header-service';
import { getRoleName } from '@app/utils/jwt';

import { APPROVAL_STATUS, canAccessApprovalQueue, canApproveRequest, decisionLabel, UPDATE_REQUESTS_CHANGED } from './utils/approval';
import { CopyValue, RequestEquipment } from './components/request-equipment';
import { requestChanges } from './utils/request-changes';
import { onlyProcessFields } from './utils/process-type';
import './request-detail.css';

const page = { limit: 100000, offset: 0, sort: '+id', filter: {} };
const unwrap = <T,>(response: { data: ApiResponse<T> }): T => {
  if (response.data.code !== 200 && response.data.code !== 201) {
    throw new Error(response.data.message || 'Не удалось выполнить запрос');
  }
  return response.data.data;
};

export const UpdateRequestDetail = ({ requestId }: { requestId?: string }) => {
  useHeader('Заявка на изменение ТТ', []);
  const params = useParams<{ id: string }>();
  const id = requestId || params.id;
  const location = useLocation<{ back?: string }>();
  const history = useHistory();
  const [request, setRequest] = useState<MasterOutletUpdate>();
  const [outlet, setOutlet] = useState<MasterOutlet>();
  const [previousId, setPreviousId] = useState<number>();
  const [names, setNames] = useState<Record<number, string>>({});
  const [loading, setLoading] = useState(true);
  const [failure, setFailure] = useState('');
  const [revision, setRevision] = useState(0);
  const [comment, setComment] = useState('');
  const [rejecting, setRejecting] = useState(false);
  const [busy, setBusy] = useState(false);
  const submitting = useRef(false);
  const role = getRoleName();
  const back = location.state?.back?.startsWith('/acl/') ? location.state.back :
    canAccessApprovalQueue(role) ? '/acl/outlet-approvals/?sort=-id' : '/acl/updates-outlets/?sort=-id';

  useEffect(() => {
    let active = true;
    setLoading(true);
    setFailure('');
    setRequest(undefined);
    setComment('');
    setRejecting(false);
    void (async () => {
      try {
        const item = onlyProcessFields(unwrap(await api.acb.getUpdate(id)));
        const [pointResponse, linksResponse, distributorsResponse] = await Promise.all([
          api.acb.getMasterOutlet(item.outlet_id),
          api.acb.getOutletDistributors(item.outlet_id, { ...page, sort: '+distributor_id' }),
          api.distributors.getDistributors(page),
        ]);
        const point = unwrap(pointResponse);
        const links = unwrap(linksResponse);
        const distributors = unwrap(distributorsResponse);
        if (!active) { return; }
        setRequest(item);
        setOutlet(point);
        setPreviousId(item.data_before?.distributors?.find((d) => d.is_general)?.id ??
          links.data.find((d) => d.is_general && d.is_active)?.distributor_id);
        setNames(Object.fromEntries(distributors.data.map((d) => [d.id, d.name || d.legal_name])));
      } catch (e) {
        if (active) { setFailure(e instanceof Error ? e.message : 'Не удалось загрузить заявку'); }
      } finally {
        if (active) { setLoading(false); }
      }
    })();
    return () => { active = false; };
  }, [id, revision]);

  const decide = async (reject: boolean) => {
    if (submitting.current || !request || !canApproveRequest(getRoleName(), request.status.id) || (reject && !comment.trim())) { return; }
    submitting.current = true;
    setBusy(true);
    setFailure('');
    try {
      unwrap(await (reject ? api.acb.rejectUpdate(id, comment.trim()) : api.acb.approveUpdate(id)));
      window.dispatchEvent(new Event(UPDATE_REQUESTS_CHANGED));
      history.push(back);
    } catch (e) {
      setFailure(e instanceof Error ? e.message : 'Не удалось сохранить решение. Повторите попытку.');
    } finally {
      submitting.current = false;
      setBusy(false);
    }
  };

  const decisions = [...(request?.history || [])].sort((a, b) => a.update_dt.localeCompare(b.update_dt));
  const approved = decisions.some((item, index) => decisionLabel(item, decisions[index - 1]) === 'Согласована');
  const rejected = decisions.some((item, index) => decisionLabel(item, decisions[index - 1]) === 'Отклонена');
  const rejectionComment = request?.comment || [...decisions].reverse().find((item) => item.status.id === 2)?.comment;
  const changes = request ? requestChanges(request, outlet, previousId, names) : [];
  const repeatApproval = decisions.filter((item) => item.status.id === APPROVAL_STATUS).length > 1;
  const statusText = request?.status.id === APPROVAL_STATUS ? (repeatApproval ? 'Повторно на согласовании' : 'Согласование') :
    request?.status.id === 2 && (rejected || rejectionComment) ? 'Отклонена' : approved ? 'Согласована' : request?.status.name;
  const formatDate = (value?: string) => value ? new Date(value).toLocaleString('ru-RU', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  }) : 'Дата не указана';

  return <div className="request-page"><div className="request-page__inner">
    {failure && <Alert status="error" mb={4}>{failure}</Alert>}
    {loading ? <Spinner aria-label="Загрузка заявки" /> : !request ?
      <Button onClick={() => setRevision((value) => value + 1)}>Повторить загрузку</Button> : <>
        <section className="request-panel request-outlet" aria-label="Торговая точка">
          <header className="request-outlet-header">
            <div className="request-title-row"><h1 className="request-title">Торговая точка</h1><span className="request-readonly">только для чтения</span></div>
            <span className={`request-status${request.status.id === 2 ? ' request-status--rejected' : approved && request.status.id !== APPROVAL_STATUS ? ' request-status--done' : ''}`}>{statusText}</span>
          </header>
          <div className="request-info-row">
            <div className="request-info"><span>ID ТТ</span><p>{request.outlet_id}</p></div>
            <div className="request-info"><span>Статус ТТ</span><p>{outlet?.status?.name || '—'}</p></div>
            <div className="request-info request-info--wide"><span>Юридическое наименование</span><p>{outlet?.legal_name || '—'}</p></div>
          </div>
          <div className="request-info-row">
            <div className="request-info request-info--wide"><span>Наименование</span><p>{outlet?.name || '—'}</p></div>
            <div className="request-info"><span>Вывеска</span><p>{outlet?.title || '—'}</p></div>
          </div>
          <div className="request-info"><span>Фактический адрес</span><p>{outlet?.address?.full_address || '—'}</p></div>
        </section>
        {request.status.id === 2 && (rejected || rejectionComment) && <Alert status="error" mb={4}>Заявка отклонена. {rejectionComment}</Alert>}
        {approved && request.status.id !== APPROVAL_STATUS && <Alert status="success" mb={4}>Заявка согласована{request.status.id === 1 ? ', ожидает обработки координатором' : ''}.</Alert>}
        <div className="request-layout">
          <div>
            <section className="request-panel" aria-label="Изменения заявки">
              <div className="request-panel__content">
                <h2 className="request-section-title">Что меняется · {changes.length} {changes.length === 1 ? 'поле' : changes.length > 1 && changes.length < 5 ? 'поля' : 'полей'}</h2>
                {changes.length ? <div className="request-changes" role="list" aria-label="Изменённые поля">
                  {changes.map((row) => <div className="request-change" role="listitem" key={row.key}>
                    <span className="request-change-label">{row.label}</span>
                    <div className="request-value request-value--before"><span>Сейчас</span><p>{row.before}</p></div>
                    <div className="request-value request-value--after"><span>Станет</span><p>{row.after}</p></div>
                  </div>)}
                </div> : <p className="request-note">Нет отличий от доступных данных ТТ.</p>}
                {!request.data_before && <p className="request-note">Значения «Сейчас» показаны по текущим данным ТТ.</p>}
              </div>
              {canApproveRequest(role, request.status.id) && <div className="request-actions">
                {rejecting && <FormControl isRequired width="100%" mb={2}>
                  <FormLabel htmlFor="rejection-comment" fontSize="sm">Причина отклонения</FormLabel>
                  <Textarea autoFocus bg="white" id="rejection-comment" value={comment} onChange={(event) => setComment(event.target.value)} isDisabled={busy} />
                </FormControl>}
                {!rejecting && <Button leftIcon={<FiCheck />} bg="#1d7c47" color="white" _hover={{ bg: '#17653a' }} height="44px" px={6} fontSize="14px" isLoading={busy} onClick={() => decide(false)}>Согласовать</Button>}
                <Button aria-label="Отклонить" variant="outline" bg="white" color="#cb2315" borderColor="#f0b2ac" _hover={{ bg: '#fff4f2' }} height="44px" px={5} fontSize="14px" isDisabled={busy || (rejecting && !comment.trim())} onClick={() => rejecting ? decide(true) : setRejecting(true)}>{rejecting ? 'Отклонить' : 'Отклонить…'}</Button>
                {rejecting && <Button height="44px" variant="ghost" isDisabled={busy} onClick={() => setRejecting(false)}>Отмена</Button>}
              </div>}
            </section>
            {!!decisions.length && <section className="request-panel request-history">
              <div className="request-panel__content">
                <h2 className="request-section-title">История заявки · {decisions.length}</h2>
                {decisions.map((item, index) => <div className="request-history-item" key={`${item.update_dt}-${index}`}>
                  <Text fontWeight="600">{decisionLabel(item, decisions[index - 1])}</Text>
                  <p className="request-history-meta">{item.user?.full_name || item.user?.username || 'Автор не указан'} · {formatDate(item.update_dt)}</p>
                  {item.comment && <Text mt={2} whiteSpace="pre-wrap">{item.comment}</Text>}
                </div>)}
              </div>
            </section>}
          </div>
          <RequestEquipment key={`${id}-${revision}`} outletId={request.outlet_id} />
        </div>
        <footer className="request-footer">
          <span>Создал <strong>{request.user?.full_name || request.user?.username || 'Автор не указан'}</strong> · {formatDate(request.create_dt)}</span>
          <div className="request-id"><span>ID заявки {request.id}</span><CopyValue value={request.id} label="Скопировать ID заявки" /></div>
        </footer>
      </>}
  </div></div>;
};
